import React, { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  listRecommendationsForLocation,
  type LocationRecommendationItem,
} from '@/api/enrichedRecommendation';
import { RecommenderAvatarImage } from '@/components/shared/RecommenderAvatarImage/RecommenderAvatarImage';
import { debug, error as logError } from '@/utils/logger';
import { isAuthRequiredError } from '@/utils/errorHelpers';
import { isMongoObjectIdString } from '@/utils/idGeneration';

/** Collapsed view: Figma shows a 2×2 grid of tips. */
const COLLAPSED_TIP_COUNT = 4;
const PAGE_SIZE = 20;

const TIP_CARD =
  'flex w-full items-center justify-between gap-2 rounded-xl border border-solid border-[#E1E1E7] bg-white p-3 text-left max-md:gap-3 max-md:p-3.5';

/** 'sign-in': logged out and the endpoint still wants a token. */
type LoadState = 'idle' | 'loading' | 'loading-more' | 'error' | 'sign-in';

const TipSkeleton: React.FC = () => (
  <div className={TIP_CARD}>
    <div className="flex min-w-0 flex-1 items-center gap-3">
      <div className="h-9 w-9 shrink-0 animate-pulse rounded-full bg-gray-200" />
      <div className="flex flex-1 flex-col gap-1.5">
        <div className="h-3.5 w-24 animate-pulse rounded bg-gray-200" />
        <div className="h-3 w-full animate-pulse rounded bg-gray-200" />
      </div>
    </div>
  </div>
);

const TipItem: React.FC<{ item: LocationRecommendationItem }> = ({ item }) => {
  const [expanded, setExpanded] = useState(false);
  const { recommendation: rec, recommender } = item;
  const name = recommender?.name ?? 'Unknown user';
  const notes = typeof rec.notes === 'string' ? rec.notes.trim() : '';
  const text = notes || 'Recommended this place.';

  return (
    <button
      type="button"
      className={clsx(TIP_CARD, 'cursor-pointer transition-colors hover:bg-[#FAFAFA]')}
      onClick={() => setExpanded((v) => !v)}
      aria-expanded={expanded}
    >
      <span className="flex min-w-0 flex-1 items-center gap-3">
        <RecommenderAvatarImage
          url={recommender?.small_avatar_url ?? recommender?.large_avatar_url}
          alt={name}
          className="h-9 w-9 shrink-0 rounded-full object-cover"
        />
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="truncate font-manrope text-sm font-semibold leading-5 tracking-[0.02em] text-[#252528] max-md:text-black">
            {name}
          </span>
          <span
            className={clsx(
              'font-manrope text-xs font-normal leading-4 tracking-[0.02em] text-[#525257]',
              !expanded && 'line-clamp-2',
              !notes && 'italic',
            )}
          >
            {text}
          </span>
        </span>
      </span>
      <ChevronRightIcon
        sx={{
          fontSize: 20,
          flexShrink: 0,
          color: '#252528',
          '@media (max-width: 767px)': { fontSize: 16 },
          transform: expanded ? 'rotate(90deg)' : 'none',
          transition: 'transform 0.2s ease',
        }}
        aria-hidden
      />
    </button>
  );
};

interface PlaceModalCommunityTipsProps {
  locationId: string | undefined;
  isLoggedIn: boolean;
  /** Bumped by the parent after recommend / unrecommend so the list refetches. */
  refreshSignal?: number;
  onReadMore?: () => void;
  onAddTip: () => void;
  onSignInClick: () => void;
  /** Rendered above the heading (mobile: the "Recommended by" facepile). */
  header?: React.ReactNode;
}

/** Figma "CommunitySection": GT recommendations as tips, 2 columns, "Read More →" expands. */
const PlaceModalCommunityTips: React.FC<PlaceModalCommunityTipsProps> = ({
  locationId,
  isLoggedIn,
  refreshSignal = 0,
  onReadMore,
  onAddTip,
  onSignInClick,
  header,
}) => {
  const [items, setItems] = useState<LocationRecommendationItem[]>([]);
  const [loadState, setLoadState] = useState<LoadState>('idle');
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [isExpanded, setIsExpanded] = useState(false);
  const seenIdsRef = useRef<Set<string>>(new Set());

  const validLocationId = isMongoObjectIdString(locationId) ? locationId : null;

  const fetchPage = useCallback(
    async (nextPage: number, isLoadMore: boolean) => {
      if (!validLocationId) return;
      setLoadState(isLoadMore ? 'loading-more' : 'loading');
      debug('[PlaceModalCommunityTips] Fetching page', { locationId: validLocationId, page: nextPage });
      try {
        const result = await listRecommendationsForLocation(validLocationId, {
          page: nextPage,
          limit: PAGE_SIZE,
          excludeAuto: true,
        });
        setItems((prev) => {
          const merged = [...prev];
          for (const item of result.items) {
            const id = item.recommendation._id;
            if (id && !seenIdsRef.current.has(id)) {
              seenIdsRef.current.add(id);
              merged.push(item);
            }
          }
          return merged;
        });
        setHasMore(result.pagination.has_more);
        setPage(nextPage);
        setLoadState('idle');
      } catch (err) {
        logError('[PlaceModalCommunityTips] Fetch failed', { locationId: validLocationId, error: err });
        setLoadState(!isLoggedIn && isAuthRequiredError(err) ? 'sign-in' : 'error');
      }
    },
    [validLocationId, isLoggedIn],
  );

  useEffect(() => {
    // Refetches on sign-in / sign-out so the response matches the viewer
    if (!validLocationId) return;
    seenIdsRef.current = new Set();
    setItems([]);
    setPage(1);
    setHasMore(false);
    setIsExpanded(false);
    void fetchPage(1, false);
  }, [isLoggedIn, validLocationId, refreshSignal, fetchPage]);

  const visibleItems = isExpanded ? items : items.slice(0, COLLAPSED_TIP_COUNT);
  const canReadMore = !isExpanded && (items.length > COLLAPSED_TIP_COUNT || hasMore);
  const linkClass =
    'border-none bg-transparent p-0 font-manrope text-xs font-semibold leading-4 tracking-[0.02em] text-[#FF5212] hover:underline disabled:opacity-50';

  return (
    <section className="flex flex-col gap-3 px-2.5 max-md:px-0">
      {header}
      <h3 className="m-0 font-lora text-lg font-semibold leading-6 tracking-[0.5px] text-[#252528]">
        Community Tips
      </h3>

      {loadState === 'sign-in' && (
        <p className="m-0 font-manrope text-sm leading-5 tracking-[0.02em] text-[#525257]">
          <button type="button" onClick={onSignInClick} className={linkClass}>
            Sign in
          </button>{' '}
          to see what fellow Globe Thrivers say about this place.
        </p>
      )}

      {loadState === 'loading' && (
        <div className="grid grid-cols-1 gap-x-6 gap-y-2 md:grid-cols-2">
          {Array.from({ length: COLLAPSED_TIP_COUNT }).map((_, i) => (
            <TipSkeleton key={i} />
          ))}
        </div>
      )}

      {loadState === 'error' && (
        <p className="m-0 font-manrope text-sm text-[#525257]">Unable to load tips. Please try again later.</p>
      )}

      {(loadState === 'idle' || loadState === 'loading-more') && items.length === 0 && (
        <p className="m-0 font-manrope text-sm leading-5 tracking-[0.02em] text-[#525257]">
          No tips yet.{' '}
          <button type="button" onClick={onAddTip} className={linkClass}>
            Be the first to share one
          </button>
        </p>
      )}

      {visibleItems.length > 0 && (
        <>
          <div className="grid grid-cols-1 items-start gap-x-6 gap-y-2 md:grid-cols-2">
            {visibleItems.map((item) => (
              <TipItem key={item.recommendation._id} item={item} />
            ))}
          </div>

          <div className="flex justify-end">
            {canReadMore && (
              <button
                type="button"
                className={linkClass}
                onClick={() => {
                  setIsExpanded(true);
                  onReadMore?.();
                }}
              >
                Read More →
              </button>
            )}
            {isExpanded && hasMore && (
              <button
                type="button"
                className={linkClass}
                disabled={loadState === 'loading-more'}
                onClick={() => void fetchPage(page + 1, true)}
              >
                {loadState === 'loading-more' ? 'Loading…' : 'Load more →'}
              </button>
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default PlaceModalCommunityTips;
